import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

export const PageHeader = ({ id, zh, desc, actions }) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    <div>
      <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-100">
        <span className="i18n-id">{id}</span> <span className="cn text-base md:text-lg text-slate-400 font-normal">{zh}</span>
      </h1>
      {desc && <p className="text-sm text-slate-500 mt-1">{desc}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

export function DataTable({ columns, rows, testid, empty = "Belum ada data / 暂无数据" }) {
  return (
    <div className="overflow-x-auto max-w-full">
      <table className="tbl" data-testid={testid}>
        <thead>
          <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center text-slate-500 py-10">{empty}</td></tr>
          ) : (
            rows.map((r, i) => (
              <tr key={r.id || i}>
                {columns.map((c) => <td key={c.key} className={c.className || ""}>{c.render ? c.render(r, i) : r[c.key]}</td>)}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const BADGE = {
  Aktif: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  Selesai: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  Draft: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  Digantikan: "bg-slate-500/15 text-slate-400 border-slate-500/30 line-through",
  Menunggu: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  OK: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  Repair: "bg-amber-500/15 text-amber-400 border-amber-500/30",
};

export const StatusBadge = ({ status }) => (
  <span className={cn("inline-block rounded border px-2 py-0.5 text-xs font-medium", BADGE[status] || "bg-slate-500/15 text-slate-300 border-slate-500/30")} data-testid={`status-badge-${String(status).toLowerCase()}`}>
    {status}
  </span>
);

export const PctChip = ({ value }) => {
  const v = Number(value) || 0;
  const cls = v >= 55 ? BADGE.OK : v >= 40 ? BADGE.Menunggu : "bg-rose-500/15 text-rose-400 border-rose-500/30";
  return <span className={cn("inline-block rounded border px-2 py-0.5 font-mono text-xs", cls)} data-testid={`pct-chip-${v}`}>{v}%</span>;
};

export function FormDialog({ open, onOpenChange, title, children, onSubmit, submitLabel = "Simpan", testid, wide = false }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("bg-[#0D1524] border-slate-800 max-h-[92vh] overflow-y-auto", wide ? "sm:max-w-4xl" : "sm:max-w-lg")}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <form onSubmit={async (e) => { e.preventDefault(); setBusy(true); try { await onSubmit(e); onOpenChange(false); } catch (err) { /* validasi gagal → dialog tetap terbuka */ } finally { setBusy(false); } }}>
          <div className="space-y-4 py-2">{children}</div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel-button`}>Batal</Button>
            <Button type="submit" disabled={busy} data-testid={`${testid}-submit-button`}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-slate-400">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export function SecureImage({ path, className, alt = "foto" }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!path) return;
    let u = null;
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); })
      .catch(() => setErr(true));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [path]);
  if (!path || err) return <div className={cn("flex items-center justify-center bg-slate-800/50 text-slate-600 text-xs rounded", className)}>{err ? "—" : "…"}</div>;
  return url ? <img src={url} alt={alt} className={cn("rounded object-cover", className)} /> : <div className={cn("animate-pulse bg-slate-800/50 rounded", className)} />;
}

export const Loading = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-40 h-1.5 rounded-full bg-slate-800 overflow-hidden">
      <div className="sweep h-full w-1/2 rounded-full bg-sky-500" />
    </div>
  </div>
);